// Close-Systemaufgaben in der Inbox: Art und Zusatzfelder, die das Backend (services/aufgaben_system.py) liefert.
import type { Aufgabe } from "../../api";
import { telefonText } from "../../werkzeuge/telefon";

/** aufgabe = Follow-up, anruf = Anrufaufgabe („Call“), alle anderen sind Systemaufgaben aus Close */
export type AufgabeArt = "aufgabe" | "anruf" | "verpasst" | "angenommen" | "voicemail" | "sms" | "opportunity";

export interface AufgabeSystemFelder {
  art?: AufgabeArt;
  /** Lesbarer Text im Wortlaut von Close („Verpasster Anruf (Simon Lempert)“); ältere Server liefern ihn nicht */
  anzeige_text?: string;
  phone?: string | null;
  phone_formatted?: string | null;
  phone_description?: string | null;
  duration?: number | null;
  voicemail_url?: string | null;
  sms_text?: string | null;
  opportunity_id?: string | null;
  opportunity_value?: number | null;
  opportunity_note?: string | null;
}

// `art` kommt hier neu (mit „anruf“); das Feld in `api/types.ts` kennt die Anrufaufgabe noch nicht (Wunsch in AENDERUNGSWUENSCHE.md)
export type AufgabeX = Omit<Aufgabe, "art"> & AufgabeSystemFelder;

export function mitArt(a: Aufgabe): AufgabeX {
  return a as AufgabeX;
}

/** Anruf, Voicemail, SMS oder Opportunity-Erinnerung aus Close (nicht vom Nutzer angelegt) */
export function istSystem(a: Aufgabe): boolean {
  const art = mitArt(a).art;
  return !!art && art !== "aufgabe" && art !== "anruf";
}

/** Anrufaufgabe (Close-Typ outgoing_call) */
export function istAnrufaufgabe(a: Aufgabe): boolean {
  return mitArt(a).art === "anruf";
}

/** Verpasster/angenommener Anruf, Voicemail, SMS oder Anrufaufgabe: es gibt eine Nummer, an der man zurückrufen kann */
export function hatNummer(a: Aufgabe): boolean {
  const x = mitArt(a);
  return !!x.phone && (x.art === "verpasst" || x.art === "angenommen" || x.art === "voicemail" || x.art === "sms" || x.art === "anruf");
}

/** Eintrag der Calls: verpasster oder angenommener Anruf ohne Lead, Voicemail. Hier gibt es den Rückruf-Knopf. */
export function istCall(a: Aufgabe): boolean {
  const art = mitArt(a).art;
  return art === "verpasst" || art === "angenommen" || art === "voicemail";
}

/** Zeile mit Telefon-Knopf (Rückruf bzw. Anrufen): offen, mit Nummer, Anruf oder Anrufaufgabe */
export function istAnrufbar(a: Aufgabe): boolean {
  return !a.is_complete && hatNummer(a) && (istCall(a) || istAnrufaufgabe(a));
}

export function nummerText(a: Aufgabe): string {
  const x = mitArt(a);
  const nr = telefonText(x.phone, x.phone_formatted);
  return x.phone_description ? `${nr} (${x.phone_description})` : nr;
}

export function anzeigeText(a: Aufgabe): string {
  return mitArt(a).anzeige_text || a.text || "Follow-up";
}
